/**
 * Email Delivery Log Service
 *
 * Data access layer for email_delivery_log table.
 * Tracks email delivery status and events for bookings.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { Json } from '../lib/database.types';

/**
 * Email type mapping
 */
export const EMAIL_TYPES: Record<number, { name: string; icon: string }> = {
  1: { name: 'Initial Email', icon: 'Mail' },
  2: { name: 'Email with Codes', icon: 'Lock' },
  3: { name: 'Email for Deliveries', icon: 'Truck' },
  4: { name: 'Cancellation Confirmation', icon: 'XCircle' },
  5: { name: 'Follow-up Email', icon: 'RefreshCw' },
  6: { name: 'Cancellation Confirmation', icon: 'XCircle' },
};

/**
 * Email event from the events JSONB array
 */
export interface EmailEvent {
  event: string;
  provider: string;
  timestamp: string;
  recipient?: string;
  message_id?: string;
}

/**
 * Email delivery log record
 */
export interface EmailDeliveryLog {
  id: string;
  booking_id: string;
  email_type: number;
  current_email: string;
  current_status: string;
  current_message_id: string | null;
  primary_provider: string;
  fallback_provider: string | null;
  last_provider_used: string;
  events: Json;
  created_at: string;
  updated_at: string;
}

/**
 * Get email delivery logs for a booking
 */
export async function getEmailDeliveryLogs(bookingId: string) {
  try {
    const { data, error } = await supabase
      .from('email_delivery_log')
      .select('*')
      .eq('booking_id', bookingId)
      .order('email_type', { ascending: true });

    if (error) throw error;

    return { data: data as EmailDeliveryLog[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get status badge class based on email status
 */
export function getEmailStatusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'delivered':
      return 'badge-success';
    case 'sent':
      return 'badge-info';
    case 'pending':
    case 'queued':
      return 'badge-warning';
    case 'bounced':
    case 'failed':
    case 'dropped':
      return 'badge-danger';
    case 'opened':
    case 'clicked':
      return 'badge-success';
    default:
      return 'badge-info';
  }
}

/**
 * Format status for display
 */
export function formatEmailStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/**
 * Get email type display name
 */
export function getEmailTypeName(emailType: number): string {
  return EMAIL_TYPES[emailType]?.name || `Email Type ${emailType}`;
}

/**
 * Get email type icon name
 */
export function getEmailTypeIcon(emailType: number): string {
  return EMAIL_TYPES[emailType]?.icon || 'Mail';
}
